import React from 'react';
import { Box, Card, CardContent, Grid, Typography, Chip } from '@mui/material';
import { Groups as GroupsIcon } from '@mui/icons-material';
import {
  normalizePatientFamilyRelatives,
  patientHasFamilyOverviewData,
  type PatientFamilyOverviewSource,
} from '../../utils/patientFamily';

type Props = {
  patient: PatientFamilyOverviewSource;
  /** Menos padding/margem quando embutido no perfil compacto */
  compact?: boolean;
};

const fieldLabelSx = { display: 'block', color: 'text.secondary' } as const;
const fieldValueSx = { fontWeight: 600, fontSize: '0.82rem' } as const;

const PatientFamilyOverviewCard: React.FC<Props> = ({ patient, compact }) => {
  if (!patientHasFamilyOverviewData(patient)) return null;

  const relatives = normalizePatientFamilyRelatives(patient.family_relatives);

  return (
    <Card
      sx={{
        borderRadius: compact ? 2 : 3,
        border: compact ? '1px solid #e2e8f0' : 'none',
        bgcolor: '#fff',
        mb: compact ? 1.25 : 2.5,
        boxShadow: compact ? 'none' : '0 4px 14px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.04)',
      }}
    >
      <CardContent sx={{ py: compact ? 1.25 : 2.5, px: compact ? 1.75 : 2.75, '&:last-child': { pb: compact ? 1.25 : 2.5 } }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: compact ? '#334155' : '#115e59',
            mb: compact ? 1 : 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            ...(compact ? { fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' } : {}),
          }}
        >
          <GroupsIcon sx={{ fontSize: 18, color: '#1d4ed8' }} />
          Família e responsáveis
        </Typography>

        <Grid container spacing={compact ? 1 : 2}>
          {patient.mother_name ? (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={fieldLabelSx}>Nome da mãe</Typography>
              <Typography variant="body2" sx={fieldValueSx}>{patient.mother_name}</Typography>
            </Grid>
          ) : null}
          {patient.father_name ? (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={fieldLabelSx}>Nome do pai</Typography>
              <Typography variant="body2" sx={fieldValueSx}>{patient.father_name}</Typography>
            </Grid>
          ) : null}
          {patient.companion_name || patient.companion_relation ? (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={fieldLabelSx}>Acompanhante</Typography>
              <Typography variant="body2" sx={fieldValueSx}>
                {[patient.companion_name, patient.companion_relation].filter(Boolean).join(' — ')}
              </Typography>
            </Grid>
          ) : null}
          {patient.guardian_name ? (
            <Grid item xs={12}>
              <Typography variant="caption" sx={fieldLabelSx}>Responsável legal</Typography>
              <Typography variant="body2" sx={fieldValueSx}>
                {patient.guardian_name}
                {patient.guardian_relation ? ` (${patient.guardian_relation})` : ''}
                {patient.guardian_phone ? ` · ${patient.guardian_phone}` : ''}
                {patient.guardian_cpf ? ` · CPF ${patient.guardian_cpf}` : ''}
              </Typography>
            </Grid>
          ) : null}
        </Grid>

        {relatives.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ ...fieldLabelSx, mb: 1 }}>
              Outros familiares
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {relatives.map((rel, idx) => (
                <Box
                  key={`${rel.name}-${idx}`}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.75,
                    py: 0.75,
                    px: 1.25,
                    borderRadius: 1.5,
                    bgcolor: '#f8fafc',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                    {rel.name}
                  </Typography>
                  {rel.relation ? (
                    <Chip label={rel.relation} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600 }} />
                  ) : null}
                  {rel.profession ? (
                    <Typography variant="caption" color="text.secondary">
                      {rel.profession}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default PatientFamilyOverviewCard;
